import {
  BillingApi,
  type BillingInterval,
  type BillingPricingTier,
  type CheckoutParamsResponse,
  type CreateCheckoutRequest,
  type CreateContributeRequest,
  type CreateDonateRequest,
  type CreatePortalSessionRequest,
  type FamilyPaymentHistoryResponse,
  type FamilyPaymentItem,
  type FamilyPaymentType,
  type OneTimeCheckoutParamsResponse,
  type PortalSessionResponse
} from 'we-tree-api-client/v2';

import { getApiConfiguration } from '@/services/SessionService';
import { ApiResult, withApiSetup } from './apiWrapper';

export type {
  BillingInterval,
  BillingPricingTier,
  CheckoutParamsResponse,
  CreateCheckoutRequest,
  CreateContributeRequest,
  CreateDonateRequest,
  CreatePortalSessionRequest,
  FamilyPaymentHistoryResponse,
  FamilyPaymentItem,
  FamilyPaymentType,
  OneTimeCheckoutParamsResponse,
  PortalSessionResponse
};

/**
 * Wrapped billing API with standardized error handling
 */
class WrappedBillingApi {
  /**
   * Owner-only: prepare Paddle.js checkout params for Family plan upgrade
   */
  async createCheckout(body: CreateCheckoutRequest): Promise<ApiResult<CheckoutParamsResponse>> {
    return withApiSetup(
      (config) => new BillingApi(config),
      getApiConfiguration,
      (api) => api.createCheckout(body)
    );
  }

  /**
   * Owner-only: mint a one-time Paddle customer portal URL
   */
  async createPortalSession(body: CreatePortalSessionRequest): Promise<ApiResult<PortalSessionResponse>> {
    return withApiSetup(
      (config) => new BillingApi(config),
      getApiConfiguration,
      (api) => api.createPortalSession(body)
    );
  }

  /**
   * Owner-only: FAMILY_PAYMENT ledger history + contribution balance
   */
  async listFamilyPayments(
    familyId: string,
    options?: { page?: number; pageSize?: number }
  ): Promise<ApiResult<FamilyPaymentHistoryResponse>> {
    return withApiSetup(
      (config) => new BillingApi(config),
      getApiConfiguration,
      (api) => api.listFamilyPayments(familyId, options?.page, options?.pageSize)
    );
  }

  /**
   * Any family member: prepare one-time contribution checkout (transactionId for Paddle.js).
   */
  async createContributeCheckout(body: CreateContributeRequest): Promise<ApiResult<OneTimeCheckoutParamsResponse>> {
    return withApiSetup(
      (config) => new BillingApi(config),
      getApiConfiguration,
      (api) => api.createContributeCheckout(body)
    );
  }

  /**
   * Any authenticated user: prepare one-time donation checkout.
   */
  async createDonateCheckout(body: CreateDonateRequest): Promise<ApiResult<OneTimeCheckoutParamsResponse>> {
    return withApiSetup(
      (config) => new BillingApi(config),
      getApiConfiguration,
      (api) => api.createDonateCheckout(body)
    );
  }
}

export const billingApi = new WrappedBillingApi();
